import { useApolloClient } from "@apollo/client";
import { type PolicyMetadata } from "@decentriq/core";
import { DatasetByHashDocument } from "@decentriq/graphql/dist/types";
import { useQuery } from "@tanstack/react-query";
import { useApiCore } from "contexts";
import { logWarning } from "utils";
import { type MediaDataRoomDataset } from "./models";
import { createMediaDataRoomDatasetsQueryKey } from "./utils";

interface MediaDataRoomDatasetsHookPayload {
  dataRoomId: string;
  skip?: boolean;
}

const useMediaDataRoomDatasets = ({
  dataRoomId,
  skip,
}: MediaDataRoomDatasetsHookPayload) => {
  const apolloClient = useApolloClient();
  const { client, getVerificationKey } = useApiCore();
  return useQuery<MediaDataRoomDataset[]>({
    enabled: !skip,
    queryFn: async () => {
      const verificationKey = await getVerificationKey();
      const mediaDataRoom = await client.getDataRoom(
        dataRoomId,
        verificationKey
      );
      const data = mediaDataRoom.state;
      if (mediaDataRoom.state.kind !== "media") {
        throw new Error("Requested data clean room is not media data room");
      }
      const policiesMetadata = await client.getDataRoomPolicies(
        dataRoomId,
        data.policies.map(({ policyId }) => policyId)
      );
      return (
        await Promise.all(
          (
            Object.values(policiesMetadata).filter(
              ({ status }) => status === "ok"
            ) as PolicyMetadata[]
          ).map(({ manifest_hash_hex: manifestHash }) =>
            apolloClient
              .query({
                query: DatasetByHashDocument,
                variables: { manifestHash },
              })
              .then(
                ({
                  data: {
                    datasetByManifestHash: { id, name },
                  },
                }) => ({ id, manifestHash, name })
              )
              .catch((error) => {
                logWarning(
                  "Unable to fetch dataset by hash",
                  manifestHash,
                  error
                );
                return null;
              })
          )
        )
      ).filter(Boolean) as MediaDataRoomDataset[];
    },
    queryKey: createMediaDataRoomDatasetsQueryKey(dataRoomId),
    retry: false,
  });
};

export default useMediaDataRoomDatasets;
